import React from 'react'
import styled, { css } from 'styled-components'
import { Transition, TransitionStatus } from 'react-transition-group'
import { toolList, Tool, ToolCategory } from '../constants/tool-list'
import getLayer from 'styles/layer'

interface TransitionProps {
  state: TransitionStatus
}

const modalWrapperAnimate = (props: TransitionProps) => {
  const { state } = props
  if (state === 'entered') {
    return css`
      opacity: 1;
      pointer-events: visible;
    `
  }
  return css`
    opacity: 0;
    pointer-events: none;
  `
}

const ModalOverlay = styled.div<TransitionProps>`
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: ${getLayer('modal')};
  padding: 40px;
  box-sizing: border-box;

  transition: all 0.3s ease-in-out;
  opacity: 0;
  pointer-events: none;
  ${modalWrapperAnimate}
`

const modalContentAnimate = (props: TransitionProps) => {
  const { state } = props
  if (state === 'entered') {
    return css`
      transform: scale(1);
      opacity: 1;
    `
  }
  return css`
    transform: scale(0.9);
    opacity: 0;
  `
}

const ModalContainer = styled.div<TransitionProps>`
  width: 100%;
  max-width: 960px;
  height: auto;
  max-height: 90vh;
  background-color: ${(p) => p.theme.WHITE};
  border-radius: 4px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 24px;
  gap: 24px;
  box-sizing: border-box;

  transition: all 0.3s ease-in-out;
  transform: scale(0.9);
  opacity: 0;
  ${modalContentAnimate}

  @media only screen and (max-width: 1200px) {
    width: 95vw;
    max-width: 900px;
    padding: 20px;
    gap: 20px;
  }

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    width: 95vw;
    max-width: 100%;
    padding: 16px;
    gap: 16px;
  }
`

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0;
`

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: bold;
  color: ${(p) => p.theme.FONT_COLOR};

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    font-size: ${(p) => p.theme.font.size.xl};
  }
`

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background-color: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${(p) => p.theme.BACKGROUND_BASE};
  }

  img {
    width: 16px;
    height: 16px;
  }
`

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const CategorySection = styled.div`
  width: 100%;
  background-color: #f8f8f8;
  border-radius: 4px;
  padding: 16px;
  gap: 16px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`

const CategoryTitle = styled.h3`
  font-size: ${(p) => p.theme.font.size.l};
  font-weight: bold;
  color: ${(p) => p.theme.FONT_COLOR};
  margin: 0;

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    font-size: ${(p) => p.theme.font.size.m};
  }
`

const ToolGrid = styled.div<{ $isOtherTools?: boolean }>`
  width: 100%;
  display: grid;
  grid-template-columns: ${(p) =>
    p.$isOtherTools
      ? 'repeat(auto-fit, minmax(120px, 1fr))'
      : 'repeat(3, 1fr)'};
  gap: 24px;
  justify-content: start;

  @media only screen and (max-width: 1200px) {
    grid-template-columns: ${(p) =>
      p.$isOtherTools
        ? 'repeat(auto-fit, minmax(110px, 1fr))'
        : 'repeat(3, 1fr)'};
    gap: 20px;
  }

  @media only screen and (max-width: 900px) {
    grid-template-columns: ${(p) =>
      p.$isOtherTools
        ? 'repeat(auto-fit, minmax(100px, 1fr))'
        : 'repeat(2, 1fr)'};
    gap: 16px;
  }

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    grid-template-columns: ${(p) =>
      p.$isOtherTools ? 'repeat(auto-fit, minmax(100px, 1fr))' : '1fr'};
    gap: 16px;
  }
`

const ToolCard = styled.div<{ $isOtherTools?: boolean }>`
  width: 100%;
  height: ${(p) => (p.$isOtherTools ? '120px' : '120px')};
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: ${(p) => p.theme.WHITE};
  border: 1px solid ${(p) => p.theme.BORDER};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  box-sizing: border-box;
  ${(p) =>
    p.$isOtherTools &&
    `
    justify-content: center;
    align-items: center;
  `}

  &:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
    transform: translateY(-4px);
    border-color: ${(p) => p.theme.MAIN};
  }

  &:active {
    transform: translateY(-2px);
  }

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    height: ${(p) => (p.$isOtherTools ? '100px' : '110px')};
  }
`

const ToolCardContent = styled.div<{ $isOtherTools?: boolean }>`
  width: 100%;
  height: ${(p) => (p.$isOtherTools ? '100%' : '88px')};
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: ${(p) => (p.$isOtherTools ? '0' : '16px')};
  box-sizing: border-box;
  ${(p) =>
    p.$isOtherTools &&
    `
    justify-content: center;
    align-items: center;
  `}

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    padding: ${(p) => (p.$isOtherTools ? '0' : '12px')};
    gap: 12px;
  }
`

const ToolHeader = styled.div<{ $isOtherTools?: boolean }>`
  display: flex;
  align-items: ${(p) => (p.$isOtherTools ? 'center' : 'center')};
  justify-content: ${(p) => (p.$isOtherTools ? 'center' : 'space-between')};
  flex-direction: ${(p) => (p.$isOtherTools ? 'column' : 'row')};
  gap: ${(p) => (p.$isOtherTools ? '8px' : '12px')};
  width: 100%;
`

const ToolIcon = styled.div<{ $isOtherTools?: boolean }>`
  width: ${(p) => (p.$isOtherTools ? '40px' : '80%')};
  max-width: ${(p) => (p.$isOtherTools ? '40px' : '250px')};
  height: ${(p) => (p.$isOtherTools ? '40px' : '26px')};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  ${(p) =>
    p.$isOtherTools &&
    `
    background-color: white;
  `}

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    max-width: ${(p) => (p.$isOtherTools ? '35px' : '200px')};
    height: ${(p) => (p.$isOtherTools ? '35px' : '22px')};
  }
`

const ToolImage = styled.img<{ $isOtherTools?: boolean }>`
  width: ${(p) => (p.$isOtherTools ? '100%' : '100%')};
  height: ${(p) => (p.$isOtherTools ? '100%' : '100%')};
  object-fit: ${(p) => (p.$isOtherTools ? 'contain' : 'contain')};
  object-position: ${(p) => (p.$isOtherTools ? 'center' : 'left center')};
`

const ToolName = styled.h4<{ $isOtherTools?: boolean }>`
  font-size: ${(p) => (p.$isOtherTools ? '13px' : '16px')};
  font-weight: ${(p) => (p.$isOtherTools ? '500' : '600')};
  color: ${(p) => p.theme.FONT_COLOR};
  margin: 0;
  line-height: 1.3;
  text-align: center;
  flex: ${(p) => (p.$isOtherTools ? 'none' : '1')};

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    font-size: ${(p) => (p.$isOtherTools ? '11px' : '14px')};
  }
`

const ToolDescription = styled.p`
  font-size: 13px;
  color: ${(p) => p.theme.FONT_COLOR_GRAY || p.theme.SECONDARY};
  margin: 0;
  line-height: 1.4;
  flex: 1;

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    font-size: 12px;
  }
`

const SupportLink = styled.button`
  display: inline-flex;
  align-items: center;
  font-size: 13px;
  color: #808080;
  font-weight: 500;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  img {
    margin-left: 4px;
    width: 12px;
    height: 12px;
  }
`

const ToolCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const NavigationIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    filter: brightness(0) saturate(100%) invert(53%) sepia(0%) saturate(0%)
      hue-rotate(140deg) brightness(91%) contrast(85%);
  }
`

const ExternalLinkIcon = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 18px;
  height: 18px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`

interface ToolListModalProps {
  isOpen: boolean
  onClose: () => void
}

const ToolListModal: React.FC<ToolListModalProps> = ({ isOpen, onClose }) => {
  const handleToolClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const handleSupportClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault()
    e.stopPropagation()
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  return (
    <Transition
      in={isOpen}
      unmountOnExit
      timeout={{ enter: 0, exit: 300 }}
      appear
    >
      {(state: TransitionStatus) => (
        <ModalOverlay
          onMouseDown={onClose}
          state={state}
          role="dialog"
          aria-label="backdrop"
        >
          <ModalContainer
            onMouseDown={(e) => e.stopPropagation()}
            state={state}
          >
            <ModalHeader>
              <ModalTitle>ツール一覧</ModalTitle>
              <CloseButton onClick={onClose} aria-label="close">
                <img src="/image/close.svg" alt="close" />
              </CloseButton>
            </ModalHeader>
            <ModalContent>
              {toolList.map((category: ToolCategory) => {
                const isOtherTools = category.category === 'その他のツール'
                return (
                  <CategorySection key={category.category}>
                    <CategoryTitle>{category.category}</CategoryTitle>
                    <ToolGrid $isOtherTools={isOtherTools}>
                      {category.tools.map((tool: Tool) => (
                        <ToolCardWrapper key={tool.name}>
                          <ToolCard
                            $isOtherTools={isOtherTools}
                            onClick={() => handleToolClick(tool.url)}
                          >
                            <ToolCardContent $isOtherTools={isOtherTools}>
                              <ToolHeader $isOtherTools={isOtherTools}>
                                <ToolIcon $isOtherTools={isOtherTools}>
                                  <ToolImage
                                    src={tool.image}
                                    alt={tool.name}
                                    $isOtherTools={isOtherTools}
                                  />
                                </ToolIcon>
                                {isOtherTools && (
                                  <ToolName $isOtherTools={isOtherTools}>
                                    {tool.name}
                                  </ToolName>
                                )}
                                {!isOtherTools && (
                                  <NavigationIcon>
                                    <img
                                      src="/image/arrow-right-gray.svg"
                                      alt="navigate"
                                    />
                                  </NavigationIcon>
                                )}
                              </ToolHeader>
                              {!isOtherTools && tool.description && (
                                <ToolDescription>
                                  {tool.description}
                                </ToolDescription>
                              )}
                            </ToolCardContent>
                            {isOtherTools && (
                              <ExternalLinkIcon>
                                <img
                                  src="/image/outside.svg"
                                  alt="external link"
                                />
                              </ExternalLinkIcon>
                            )}
                          </ToolCard>
                          {!isOtherTools && tool.supportPageUrl && (
                            <SupportLink
                              onClick={(e) =>
                                handleSupportClick(e, tool.supportPageUrl!)
                              }
                            >
                              サポートページ
                              <img
                                src="/image/outside.svg"
                                alt="external link"
                              />
                            </SupportLink>
                          )}
                        </ToolCardWrapper>
                      ))}
                    </ToolGrid>
                  </CategorySection>
                )
              })}
            </ModalContent>
          </ModalContainer>
        </ModalOverlay>
      )}
    </Transition>
  )
}

export default ToolListModal
